import { merge } from 'lodash-es';

import { DEFAULT_FORMATTER } from '../../shared/constants.js';
import {
  defaultToolbarInternalConfig,
  type MeterBarToolbarConfig,
  type MeterBarToolbarInternalProps,
  type MeterBarToolbarSlotProps,
} from '../../shared/types/meter-bar-toolbar.js';
import { getNumericValue } from '../../shared/utils/get-numeric-value.js';
import { defaultConfig } from '../default.js';
import type {
  MultiMeterBarChartConsumerConfig,
  MultiMeterBarChartSlots,
} from '../types/multi-meter-bar-chart-config.js';
import type { MultiMeterBarChartInternalConfig } from '../types/multi-meter-bar-chart-internal.js';
import {
  type MultiMeterBarChartProps,
  type MultiMeterBarChartTooltipVariant,
  multiMeterBarChartTooltipVariantOptions,
} from '../types/multi-meter-bar-chart.js';

const getToolbarConfig = (
  toolbarSlotsConfig?: MeterBarToolbarSlotProps,
  toolbarConsumerConfig?: MeterBarToolbarConfig,
): MeterBarToolbarInternalProps => {
  const configuredToolbar = toolbarSlotsConfig ?? toolbarConsumerConfig;

  return { ...defaultToolbarInternalConfig, ...configuredToolbar };
};

const getMaxValue = (
  totalValue: number,
  maxProperty?: number | 'auto',
  consumerConfigMax?: number,
) => {
  if (typeof maxProperty === 'number') {
    return maxProperty;
  }

  if (maxProperty === 'auto') {
    return totalValue;
  }

  if (!maxProperty && consumerConfigMax) {
    return consumerConfigMax;
  }

  return totalValue;
};

const isValidTooltipVariant = (
  tooltipVariant: MultiMeterBarChartTooltipVariant,
): tooltipVariant is MultiMeterBarChartTooltipVariant =>
  multiMeterBarChartTooltipVariantOptions.includes(tooltipVariant);

const getTooltipConfig = (
  slots: MultiMeterBarChartSlots,
  consumerConfig: MultiMeterBarChartConsumerConfig,
) => {
  let consumerTooltip:
    | { hidden?: boolean; variant?: MultiMeterBarChartTooltipVariant }
    | undefined;
  if (consumerConfig.tooltip) {
    const { hidden, variant } = consumerConfig.tooltip;
    // TODO [APPDEV-17990] Remove the enabled fallback branch.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { enabled } = consumerConfig.tooltip;
    const resolvedHidden =
      hidden !== undefined
        ? hidden
        : enabled !== undefined
        ? !enabled
        : undefined;
    consumerTooltip = {
      ...(resolvedHidden !== undefined && { hidden: resolvedHidden }),
      variant,
    };
  }
  const merged = merge(
    {},
    defaultConfig.tooltip,
    consumerTooltip,
    slots.tooltip,
  );
  const variant = isValidTooltipVariant(merged.variant)
    ? merged.variant
    : defaultConfig.tooltip.variant;

  return { ...merged, variant };
};

export const buildMultiMeterBarChartConfig = (
  props: MultiMeterBarChartProps,
  consumerConfig: MultiMeterBarChartConsumerConfig,
  slots: MultiMeterBarChartSlots,
): MultiMeterBarChartInternalConfig => {
  const size = props.size ?? consumerConfig.size ?? defaultConfig.size;

  const colorPalette =
    props.colorPalette ??
    consumerConfig.colorPalette ??
    defaultConfig.colorPalette;

  const min = props.min ?? consumerConfig.min ?? defaultConfig.min;

  const tooltip = getTooltipConfig(slots, consumerConfig);

  const formatter =
    props.formatter ?? consumerConfig.formatter ?? DEFAULT_FORMATTER;
  const unit = props.unit ?? consumerConfig.unit;

  const legend = slots.legend ?? consumerConfig.legend ?? defaultConfig.legend;

  const totalValue = slots.segments.reduce((acc, segment) => {
    const value = getNumericValue(segment.value, segment.valueAccessor);
    return acc + value;
  }, 0);

  const max = getMaxValue(totalValue, props.max, consumerConfig.max);

  const colorRules = slots.colorRules ?? consumerConfig.colorRules ?? [];

  const toolbar = getToolbarConfig(slots.toolbar, consumerConfig.toolbar);

  const loading =
    props.loading ?? consumerConfig.loading ?? defaultConfig.loading;

  return {
    segments: slots.segments,
    colorPalette,
    size,
    min,
    max,
    loading,
    tooltip,
    labelSlot: slots.label,
    valueSlot: slots.value,
    minSlot: slots.min,
    maxSlot: slots.max,
    legend,
    formatter,
    unit,
    totalValue,
    colorRules,
    toolbar,
  };
};
